import { authDevicePayload } from '../api/billing'
import type { LoginResponseRaw, TokenRefreshResponseRaw } from '@provisium/web-api'

import { isOperatorUser, mapApiRedirectPath } from '../auth/routing'
import { apiRequest, setAccessToken } from './client'
import { fetchMe } from './me'
import type { AuthSession } from './types'

type LoginRequest = {
  email: string
  password: string
  device_id: string
  device_label?: string
  force?: boolean
}

export type LoginResult = {
  session: AuthSession
  redirectPath: string
}

const normalizeLoginEmail = (email: string): string => {
  const trimmed = email.trim().toLowerCase()
  return trimmed.includes('@') ? trimmed : `${trimmed}@provisium.local`
}

export const refreshAccessToken = async (refreshToken: string) => {
  const device = authDevicePayload()
  const response = await apiRequest<TokenRefreshResponseRaw>('/auth/refresh', {
    method: 'POST',
    body: { refresh_token: refreshToken, ...device },
    auth: false,
  })
  return {
    accessToken: response.access_token,
    refreshToken: response.refresh_token ?? refreshToken,
  }
}

export const login = async (
  email: string,
  password: string,
  force = false,
): Promise<LoginResult> => {
  const normalizedEmail = normalizeLoginEmail(email)
  const device = authDevicePayload()

  const response = await apiRequest<LoginResponseRaw>('/auth/login', {
    method: 'POST',
    body: {
      email: normalizedEmail,
      password,
      ...device,
      ...(force ? { force: true } : {}),
    } satisfies LoginRequest,
    auth: false,
  })

  setAccessToken(response.access_token)

  // /me разбираем тем же fetchMe: сервер отдаёт display_name, а не displayName.
  const me = await fetchMe()
  const organization = me.organizations[0]
  const operatorAccount = isOperatorUser(me.roles)

  return {
    session: {
      accessToken: response.access_token,
      refreshToken: response.refresh_token,
      organizationId: organization?.id ?? null,
      email: me.email,
      displayName: me.displayName,
    },
    redirectPath: operatorAccount ? '/operator' : mapApiRedirectPath(response.redirect_path),
  }
}

export const logout = async (session: AuthSession | null) => {
  if (!session?.refreshToken) {
    setAccessToken(null)
    return
  }
  const device = authDevicePayload()
  try {
    await apiRequest<void>('/auth/logout', {
      method: 'POST',
      body: { refresh_token: session.refreshToken, device_id: device.device_id },
    })
  } catch {
    // Client clears local session even if revoke fails.
  }
  setAccessToken(null)
}

export type QuickRegisterAccountRequest = {
  email: string
  code: string
  password: string
  consent_offer: boolean
  marketing_consent?: boolean
  notifications_consent?: boolean
}

export type QuickRegisterAccountResult = {
  accessToken: string
  refreshToken: string
  redirectPath: string
}

export const requestRegistrationCode = (email: string) =>
  apiRequest<{ message: string }>('/auth/registration-code', {
    method: 'POST',
    body: { email: normalizeLoginEmail(email) },
    auth: false,
  })

export const completeQuickRegistration = async (
  request: QuickRegisterAccountRequest,
): Promise<QuickRegisterAccountResult> => {
  const response = await apiRequest<{
    access_token: string
    refresh_token: string
    redirect_path: string
  }>('/auth/registration/complete', {
    method: 'POST',
    body: { ...request, email: normalizeLoginEmail(request.email) },
    auth: false,
  })
  return {
    accessToken: response.access_token,
    refreshToken: response.refresh_token,
    redirectPath: response.redirect_path?.trim() || '/my-prices',
  }
}

export const requestPasswordReset = async (email: string) => {
  const normalizedEmail = normalizeLoginEmail(email)
  return apiRequest<{ message?: string }>('/auth/forgot-password', {
    method: 'POST',
    body: { email: normalizedEmail },
    auth: false,
  })
}

export const confirmPasswordReset = async (token: string, password: string) => {
  return apiRequest<{ message?: string }>('/auth/reset-password', {
    method: 'POST',
    body: { token, password },
    auth: false,
  })
}
